import React, { useCallback, useEffect, useState } from "react";
import { styled } from "styled-components";

import { Heading } from "../../components/Heading";
import { Flex, HexButton, HexInputGroup, Txt } from "../../hex-components";

import { CliAuthWorkspaceSelect } from "./CliAuthWorkspaceSelect";

export namespace CliAuthFlowScreen {
  export const ConfirmUserCode = React.memo(function ConfirmCode({
    onAuthorize,
    userCode: providedUserCode,
  }: {
    userCode?: string | null;
    onAuthorize: (userCode: string, approve: boolean) => Promise<void>;
  }) {
    const [isPending, setIsPending] = useState(false);
    const [userCode, setUserCode] = useState(providedUserCode ?? "");
    useEffect(() => {
      if (providedUserCode) {
        setUserCode(providedUserCode);
      }
    }, [providedUserCode]);

    const onAction = useCallback(
      async (isApprove: boolean) => {
        setIsPending(true);
        try {
          await onAuthorize(userCode, isApprove);
        } finally {
          setIsPending(false);
        }
      },
      [onAuthorize, userCode],
    );

    return (
      <>
        <Heading renderAs="h1">Authorize the Hex CLI</Heading>
        <Flex align="stretch" css="margin: 16px 0" direction="column" gap={12}>
          <Txt>The Hex CLI is requesting access to your workspace.</Txt>

          <CliAuthWorkspaceSelect />
        </Flex>

        {providedUserCode ? (
          <>
            <Txt>Confirm this code matches your terminal:</Txt>
            <UserCodeDisplay>{userCode}</UserCodeDisplay>
          </>
        ) : (
          <>
            <Txt>Enter the code from your terminal:</Txt>
            <UserCodeInput
              autoFocus={true}
              disabled={isPending}
              id="user-code-input"
              large={true}
              placeholder="XXXX-XXXX"
              value={userCode}
              // eslint-disable-next-line react/jsx-no-bind -- ok
              onChange={(e) => setUserCode(e.target.value.toUpperCase())}
            />
          </>
        )}

        <Flex css="margin-top: 16px;" gap={8} justify="end">
          {/* eslint-disable-next-line react/jsx-no-bind -- ok */}
          <HexButton disabled={isPending} onClick={() => onAction(false)}>
            Deny
          </HexButton>
          <HexButton
            disabled={isPending}
            intent="primary"
            // eslint-disable-next-line react/jsx-no-bind -- ok
            onClick={() => onAction(true)}
          >
            Approve
          </HexButton>
        </Flex>
      </>
    );
  });

  export const Approved = React.memo(function Approved() {
    return (
      <>
        <Heading renderAs="h1">Authorized</Heading>
        <Txt>
          You have successfully authorized the Hex CLI. You can close this
          window and return to your terminal.
        </Txt>
      </>
    );
  });

  export const Denied = React.memo(function Denied() {
    return (
      <>
        <Heading renderAs="h1">Authorization denied</Heading>
        <Txt>You have denied CLI authorization. You can close this window.</Txt>
      </>
    );
  });
}

const UserCodeDisplay = styled.div`
  padding: 15px 25px;
  border-radius: ${({ theme }) => theme.borderRadius};
  background: ${({ theme }) => theme.backgroundColor.MUTED};
  border: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  font-family: monospace;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 4px;
`;

const UserCodeInput = styled(HexInputGroup)`
  input {
    font-family: monospace;
    font-size: 18px;
    letter-spacing: 2px;
  }
`;
